import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  FaultStatus,
  FeedbackCreate,
  Message,
  MessageCounts,
  Thread,
  ThreadCreate,
  ThreadSummary,
} from '@vuokramani/shared';
import { api } from './api.ts';

/** owner = vuokranantajan näkymä, party = vuokralaisen näkymä. */
export type MessagesAs = 'owner' | 'party';

/** Uudet viestit näkyvät ilman sivun latausta: lista ja luvut päivittyvät minuutin välein. */
const POLL_MS = 60_000;

function refreshAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['threads'] });
  void queryClient.invalidateQueries({ queryKey: ['threadCounts'] });
}

export function useThreads(as: MessagesAs, propertyId?: string) {
  return useQuery({
    queryKey: ['threads', as, propertyId ?? null],
    queryFn: () =>
      api<{ threads: ThreadSummary[] }>(
        `/threads?as=${as}${propertyId ? `&propertyId=${propertyId}` : ''}`,
      ).then((r) => r.threads),
    refetchInterval: POLL_MS,
  });
}

export function useThreadCounts(as: MessagesAs, enabled = true) {
  return useQuery({
    queryKey: ['threadCounts', as],
    queryFn: () => api<MessageCounts>(`/threads/counts?as=${as}`),
    refetchInterval: POLL_MS,
    enabled,
  });
}

export function useThread(id: string | undefined) {
  return useQuery({
    queryKey: ['thread', id],
    queryFn: () => api<Thread>(`/threads/${id}`),
    enabled: Boolean(id),
    refetchInterval: POLL_MS,
  });
}

/** Ketju avattu: merkitään luetuksi. */
export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/threads/${id}/read`, { method: 'POST' }),
    onSuccess: () => refreshAll(queryClient),
  });
}

export function useCreateThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leaseId, body }: { leaseId: string; body: ThreadCreate }) =>
      api<Thread>(`/leases/${leaseId}/threads`, { method: 'POST', body }),
    onSuccess: () => refreshAll(queryClient),
  });
}

export function useSendMessage(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api<Message>(`/threads/${threadId}/messages`, { method: 'POST', body: { body } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      refreshAll(queryClient);
    },
  });
}

export function useSetFaultStatus(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: FaultStatus) =>
      api<Thread>(`/threads/${threadId}`, { method: 'PATCH', body: { status } }),
    onSuccess: (thread) => {
      queryClient.setQueryData(['thread', threadId], thread);
      refreshAll(queryClient);
    },
  });
}

/** Apua-lomake ylläpidolle. */
export function useSendFeedback() {
  return useMutation({
    mutationFn: (body: FeedbackCreate) => api<void>('/feedback', { method: 'POST', body }),
  });
}
